import React, {
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";

import {
  doc,
  getDoc,
  getFirestore,
} from "firebase/firestore";

import {
  FiAlertCircle,
  FiArrowRight,
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiShield,
} from "react-icons/fi";

import logo from "../../assets/logo8.png";

const Login = () => {
  const navigate = useNavigate();

  const auth = useMemo(() => {
    return getAuth();
  }, []);

  const db = useMemo(() => {
    return getFirestore(auth.app);
  }, [auth]);

  const [email, setEmail] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const clearError = () => {
    if (error) {
      setError("");
    }
  };

  const handleLogin = async (
    event
  ) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    const normalizedEmail = email
      .trim()
      .toLowerCase();

    if (!normalizedEmail) {
      setError(
        "Имэйл хаягаа оруулна уу."
      );

      return;
    }

    if (!password) {
      setError(
        "Нууц үгээ оруулна уу."
      );

      return;
    }

    setError("");
    setLoading(true);

    try {
      const userCredential =
        await signInWithEmailAndPassword(
          auth,
          normalizedEmail,
          password
        );

      const user =
        userCredential.user;

      const parkId = String(
        user.displayName || ""
      ).trim();

      if (!parkId) {
        await signOut(auth);

        setError(
          "Хэрэглэгчийн зогсоолын мэдээлэл тохируулагдаагүй байна."
        );

        return;
      }

      const userEmail =
        user.email ||
        normalizedEmail;

      const userReference = doc(
        db,
        "parkings",
        parkId,
        "users",
        userEmail
      );

      const userSnapshot =
        await getDoc(
          userReference
        );

      if (!userSnapshot.exists()) {
        await signOut(auth);

        setError(
          "Хэрэглэгчийн мэдээлэл олдсонгүй."
        );

        return;
      }

      const userData =
        userSnapshot.data() || {};

      const role = String(
        userData.role || ""
      )
        .trim()
        .toLowerCase();

      if (!role) {
        await signOut(auth);

        setError(
          "Системд нэвтрэх эрх тохируулагдаагүй байна. Администраторт хандана уу."
        );

        return;
      }

      const destinationPath =
        role === "cass"
          ? "/paymentDashboard"
          : "/dashboard";

      navigate(destinationPath, {
        replace: true,
      });
    } catch (loginError) {
      console.error(
        "Login Error:",
        loginError
      );

      switch (
        loginError?.code
      ) {
        case "auth/invalid-email":
          setError(
            "Имэйл хаягийн формат буруу байна."
          );
          break;

        case "auth/invalid-credential":
        case "auth/user-not-found":
        case "auth/wrong-password":
          setError(
            "Имэйл эсвэл нууц үг буруу байна."
          );
          break;

        case "auth/user-disabled":
          setError(
            "Хэрэглэгчийн эрх идэвхгүй болсон байна."
          );
          break;

        case "auth/too-many-requests":
          setError(
            "Хэт олон удаа оролдлоо. Түр хүлээгээд дахин оролдоно уу."
          );
          break;

        case "auth/network-request-failed":
          setError(
            "Интернэт холболтоо шалгана уу."
          );
          break;

        case "permission-denied":
        case "firestore/permission-denied":
          setError(
            "Хэрэглэгчийн мэдээлэл унших эрх хүрэлцэхгүй байна."
          );
          break;

        default:
          setError(
            "Нэвтрэх үед алдаа гарлаа. Дахин оролдоно уу."
          );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[99999] tw-overflow-y-auto tw-bg-[#080f1d] tw-font-sans">
      {/* Minimal background light */}
      <div className="tw-pointer-events-none tw-fixed tw-inset-0 tw-overflow-hidden">
        <div className="tw-absolute tw-left-1/2 tw-top-[-420px] tw-h-[720px] tw-w-[720px] tw--translate-x-1/2 tw-rounded-full tw-bg-blue-500/10 tw-blur-[140px]" />
      </div>

      <main className="tw-relative tw-z-10 tw-flex tw-min-h-[100dvh] tw-items-center tw-justify-center tw-p-4 sm:tw-p-6">
        <div className="tw-w-full tw-max-w-[430px]">


          {/* Login card */}
          <section className="tw-rounded-[24px] tw-border tw-border-white/10 tw-bg-white tw-p-6 tw-shadow-[0_30px_90px_rgba(0,0,0,0.35)] sm:tw-p-9">
            {/* Logo */}
            <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
              <div className="tw-flex tw-h-36 tw-w-full tw-items-center tw-justify-center sm:tw-h-40">
                <img
                  src={logo}
                  alt="iParking logo"
                  width="360"
                  height="360"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  draggable="false"
                  className="
                      tw-h-auto
                      tw-w-[200px]
                      tw-max-w-full
                      tw-select-none
                      tw-object-contain
                      sm:tw-w-[250px]
                    "
                />
              </div>

              <h1 className="tw-mt-1 tw-text-[26px] tw-font-bold tw-tracking-[-0.03em] tw-text-slate-950">
                IPARKING
              </h1>

              <p className="tw-mt-2 tw-max-w-[300px] tw-text-sm tw-leading-6 tw-text-slate-500">
                Ухаалаг зогсоолын шийдэл
              </p>
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                aria-live="polite"
                className="tw-mt-6 tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-rose-200 tw-bg-rose-50 tw-p-3.5"
              >
                <FiAlertCircle
                  size={18}
                  className="tw-mt-0.5 tw-shrink-0 tw-text-rose-500"
                />

                <p className="tw-text-[13px] tw-font-medium tw-leading-5 tw-text-rose-700">
                  {error}
                </p>
              </div>
            )}

            {/* Form */}
            <form
              onSubmit={handleLogin}
              className="tw-mt-7 tw-space-y-5"
            >
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="tw-mb-2 tw-block tw-text-xs tw-font-semibold tw-text-slate-700"
                >
                  Имэйл хаяг
                </label>

                <div className="tw-group tw-relative">
                  <FiMail
                    size={18}
                    className="tw-pointer-events-none tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400 tw-transition-colors group-focus-within:tw-text-blue-600"
                  />

                  <input
                    id="email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoFocus
                    required
                    disabled={loading}
                    value={email}
                    onChange={(event) => {
                      setEmail(
                        event.target.value
                      );

                      clearError();
                    }}
                    placeholder="Имэйл хаягаа оруулна уу"
                    className="
                      tw-h-[52px]
                      tw-w-full
                      tw-rounded-xl
                      tw-border
                      tw-border-slate-200
                      tw-bg-white
                      tw-py-3
                      tw-pl-12
                      tw-pr-4
                      tw-text-sm
                      tw-font-medium
                      tw-text-slate-900
                      tw-outline-none
                      tw-transition-all
                      placeholder:tw-font-normal
                      placeholder:tw-text-slate-400
                      hover:tw-border-slate-300
                      focus:tw-border-blue-500
                      focus:tw-ring-4
                      focus:tw-ring-blue-500/10
                      disabled:tw-cursor-not-allowed
                      disabled:tw-bg-slate-50
                      disabled:tw-opacity-60
                    "
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="tw-mb-2 tw-block tw-text-xs tw-font-semibold tw-text-slate-700"
                >
                  Нууц үг
                </label>

                <div className="tw-group tw-relative">
                  <FiLock
                    size={18}
                    className="tw-pointer-events-none tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400 tw-transition-colors group-focus-within:tw-text-blue-600"
                  />

                  <input
                    id="password"
                    name="password"
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    autoComplete="current-password"
                    required
                    disabled={loading}
                    value={password}
                    onChange={(event) => {
                      setPassword(
                        event.target.value
                      );

                      clearError();
                    }}
                    placeholder="Нууц үгээ оруулна уу"
                    className="
                      tw-h-[52px]
                      tw-w-full
                      tw-rounded-xl
                      tw-border
                      tw-border-slate-200
                      tw-bg-white
                      tw-py-3
                      tw-pl-12
                      tw-pr-14
                      tw-text-sm
                      tw-font-medium
                      tw-text-slate-900
                      tw-outline-none
                      tw-transition-all
                      placeholder:tw-font-normal
                      placeholder:tw-text-slate-400
                      hover:tw-border-slate-300
                      focus:tw-border-blue-500
                      focus:tw-ring-4
                      focus:tw-ring-blue-500/10
                      disabled:tw-cursor-not-allowed
                      disabled:tw-bg-slate-50
                      disabled:tw-opacity-60
                    "
                  />

                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      setShowPassword(
                        (previous) =>
                          !previous
                      );
                    }}
                    aria-label={
                      showPassword
                        ? "Нууц үг нуух"
                        : "Нууц үг харах"
                    }
                    aria-pressed={
                      showPassword
                    }
                    className="tw-absolute tw-right-2 tw-top-1/2 tw-flex tw-h-10 tw-w-10 tw--translate-y-1/2 tw-items-center tw-justify-center tw-rounded-lg tw-text-slate-400 tw-outline-none tw-transition-colors hover:tw-bg-slate-100 hover:tw-text-slate-700 focus-visible:tw-ring-2 focus-visible:tw-ring-blue-500 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                  >
                    {showPassword ? (
                      <FiEyeOff
                        size={18}
                      />
                    ) : (
                      <FiEye
                        size={18}
                      />
                    )}
                  </button>
                </div>
              </div>

              {/* Login button */}
              <button
                type="submit"
                disabled={loading}
                className="
                  tw-group
                  tw-flex
                  tw-h-[52px]
                  tw-w-full
                  tw-items-center
                  tw-justify-center
                  tw-gap-2.5
                  tw-rounded-xl
                  tw-bg-[#0f5bd8]
                  tw-px-5
                  tw-text-sm
                  tw-font-semibold
                  tw-text-white
                  tw-shadow-[0_12px_25px_rgba(15,91,216,0.22)]
                  tw-outline-none
                  tw-transition-all
                  hover:tw-bg-[#0b4fbe]
                  hover:tw-shadow-[0_15px_30px_rgba(15,91,216,0.28)]
                  focus-visible:tw-ring-4
                  focus-visible:tw-ring-blue-500/25
                  active:tw-scale-[0.99]
                  disabled:tw-cursor-not-allowed
                  disabled:tw-bg-slate-300
                  disabled:tw-text-slate-500
                  disabled:tw-shadow-none
                "
              >
                {loading ? (
                  <>
                    <span className="tw-h-5 tw-w-5 tw-animate-spin tw-rounded-full tw-border-[3px] tw-border-white/30 tw-border-t-white" />

                    <span>
                      Нэвтэрч байна...
                    </span>
                  </>
                ) : (
                  <>
                    <span>
                      Нэвтрэх
                    </span>

                    <FiArrowRight
                      size={18}
                      className="tw-transition-transform group-hover:tw-translate-x-1"
                    />
                  </>
                )}
              </button>
            </form>

            {/* Security */}
            <div className="tw-mt-6 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border-t tw-border-slate-100 tw-pt-5">
              <FiShield
                size={14}
                className="tw-text-slate-400"
              />

              <p className="tw-text-center tw-text-[11px] tw-font-medium tw-text-slate-400">
                © {new Date().getFullYear()} iParking · Зохиогчийн эрх хуулиар хамгаалагдсан
              </p>
            </div>
          </section>

    
        </div>
      </main>
    </div>
  );
};

export default Login;
